import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import { Button } from 'antd';
import type { CredStepKey } from '../../steps';
import { renderSectionView } from '../../reviewSpec';
import s from '../../s.module.less';

const AttentionIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="#92400E" aria-hidden>
        <path
            fillRule="evenodd"
            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
        />
    </svg>
);

interface Props {
    // 向导 7 步传 CredStepKey（只读态走 renderSectionView）；自定义板块传任意标识 + viewContent
    sectionKey: CredStepKey | string;
    title: string;
    values?: Record<string, any>;
    // 自定义只读内容：提供时替代 renderSectionView（Practice enrollment / payer-requirements 板块用）
    viewContent?: React.ReactNode;
    editing: boolean;
    hasErrors?: boolean;
    saving?: boolean;
    // 宿主页面有 77px 固定底部导航时传 true，吸底的 Save/Cancel 条抬到它上方（弹窗内不传）
    footerOffset?: boolean;
    // Settings 弹窗场景传 true：编辑态 Cancel/Save 放进吸顶标题行，不再渲染表单末尾的吸底条；
    // Review 页保持默认（吸底 Save bar）
    actionsInHeader?: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    children?: React.ReactNode;
}

export default function ReviewSection({
    sectionKey,
    title,
    values,
    viewContent,
    editing,
    hasErrors,
    saving,
    footerOffset,
    actionsInHeader,
    onEdit,
    onCancel,
    onSave,
    children,
}: Props) {
    const sectionRef = useRef<HTMLDivElement>(null);

    // 进入编辑时把当前板块滚到滚动容器顶部（页面场景预留顶部导航高度），配合吸顶标题/吸底 Save bar
    useEffect(() => {
        if (editing) sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [editing]);

    return (
        <div
            ref={sectionRef}
            className={cx(s.reviewSection, hasErrors && s.reviewSectionErrors, editing && s.reviewSectionEditing)}
            style={{ scrollMarginTop: footerOffset ? 96 : 16 }}
            data-section={sectionKey}
        >
            {/* V2：编辑时标题吸顶、Save/Cancel 移到表单末尾吸底，长 section 滚动中始终知道在编辑什么、随时能保存；
                review 页（footerOffset）有 sticky topbar，吸顶点要落到它下方 */}
            <div
                className={cx(
                    s.reviewSectionHeader,
                    editing && s.editingHeader,
                    editing && footerOffset && s.editingHeaderPage,
                )}
            >
                <div className={s.reviewSectionTitle}>
                    {title}
                    {hasErrors && (
                        <span className={s.attentionBadge}>
                            <AttentionIcon />
                            Attention required
                        </span>
                    )}
                </div>
                {!editing && (
                    <button type="button" className={s.reviewEditBtn} onClick={onEdit}>
                        Edit
                    </button>
                )}
                {editing && actionsInHeader && (
                    <div className={s.reviewEditActions}>
                        <Button onClick={onCancel} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={onSave} loading={saving}>
                            Save changes
                        </Button>
                    </div>
                )}
            </div>

            {editing ? (
                <div className={s.reviewEditForm}>
                    {children}
                    {!actionsInHeader && (
                        <div className={s.stickySaveBar}>
                            <Button onClick={onCancel} disabled={saving}>
                                Cancel
                            </Button>
                            <Button type="primary" onClick={onSave} loading={saving}>
                                Save changes
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                (viewContent ?? renderSectionView(sectionKey as CredStepKey, values || {}))
            )}
        </div>
    );
}
